import React, { FC, createContext, useState } from "react";

import { IMessage } from "../interfaces";

interface IAlertBoxState {
    isBoxActive: boolean, 
    pendingFunctionName: string,
    message: string
}

type TAnnouncementStates = 
"dilemmaOwnerAnouncement" | 
"renderDilemmaCard" | 
"renderTippingHeart" |
"";

interface IBoardContext {
    isSurveyShown: boolean;
    isStatisticsShown: boolean;
    isGameInPostGamePhase: boolean;
    isGoodbyePageShown: boolean; 
    chatMessages: IMessage[];
    isSpinClicked: boolean;
    jokerClicked: boolean;
    isUserInPhaseSeven: boolean;
    didUserRejoinTheGame: boolean;
    isWheelSpinningDone: boolean;
    isInitialSolutionDecksModalOpen: boolean;
    isAvatarCheering: boolean;
    renderDilemmaCardsInThisPhase: boolean;
    renderInitialCoins: boolean;
    displayModalData: any;
    areSolutionCardsDrawnContext: boolean;
    isStrengthCardsComponentShown: boolean; 
    isWinnersPodiumShown: boolean; 
    soundVolume: number; 
    areYouSureBoxRendered: IAlertBoxState;
    instructionTutorial: string;
    isDilemmaOwnerAnouncementDone: TAnnouncementStates;
    isCurrentlyPhase16: boolean;
    areStrengthsShownFullScreen: boolean;
    sendMessageToTheUser: any, 
    showStatistics(): void;
    setChatMessages(chatMessages: IMessage[]): void;
    setIsSpinClicked(isSpinClicked: boolean): void;
    setJokerClicked(jokerClicked: boolean): void;
    setIsUserInPhaseSeven(isUserInPhaseSeven: boolean): void;
    setDidUserRejoinTheGame(didUserRejoinTheGame: boolean): void;
    setIsWheelSpinningDone(isWheelSpinningDone: boolean): void;
    setIsInitialSolutionDecksModalOpen(isInitialSolutionDecksModalOpen: boolean): void;
    setIsAvatarCheering(isAvatarCheering: boolean): void;
    setRenderDilemmaCardsInThisPhase(renderDilemmaCardsInThisPhase: boolean): void;
    setRenderInitialCoins(renderInitialCoins: boolean): void;
    setDisplayModalData(displayModalData: any): void;
    setisGameInPostGamePhase(isGameInPostGamePhase: boolean): void;
    setIsGoodbyePageShown(isGoodbyePageShown: boolean): void;
    setAreSolutionCardsDrawnContext(areSolutionCardsDrawnContext: boolean): void;
    setIsStrengthCardsComponentShown(isStrengthCardsComponentShown: boolean): void;
    setIsWinnersPodiumShown(isWinnersPodiumShown: boolean): void;
    setSoundVolume(soundVolume: number): void;
    setAreYouSureBoxRendered(areYouSureBoxRendered: IAlertBoxState): void;
    setInstructionTutorial(instructionTutorial: string): void;
    setIsDilemmaOwnerAnouncementDone(isDilemmaOwnerAnouncementDone: TAnnouncementStates): void;
    setIsCurrentlyPhase16(isCurrentlyPhase16: boolean): void;
    setAreStrengthsShownFullScreen(areStrengthsShownFullScreen: boolean): void;
    setIsSurveyShown(isSurveyShown: boolean): void;
    setSendMessageToTheUser(sendMessageToTheUser: any): void;
};

const BoardContext = createContext({} as IBoardContext);

interface BoardContextProps {
    children: React.ReactNode;
};

const BoardContextProvider: FC<BoardContextProps> = (props) => {
    const [isSurveyShown, setIsSurveyShown] = useState<boolean>(false);
    const [isStatisticsShown, setIsStatisticsShown] = useState<boolean>(false);
    const [isGameInPostGamePhase, setisGameInPostGamePhase] = useState<boolean>(false);
    const [isGoodbyePageShown, setIsGoodbyePageShown] = useState<boolean>(false);
    const [chatMessages, setChatMessages] = useState<IMessage[]>([]);
    const [isSpinClicked, setIsSpinClicked] = useState<boolean>(false);
    const [jokerClicked, setJokerClicked] = useState<boolean>(false);
    const [isUserInPhaseSeven, setIsUserInPhaseSeven] = useState<boolean>(false);
    const [didUserRejoinTheGame, setDidUserRejoinTheGame] = useState<boolean>(false);
    const [isWheelSpinningDone, setIsWheelSpinningDone] = useState<boolean>(false);
    const [isInitialSolutionDecksModalOpen, setIsInitialSolutionDecksModalOpen] = useState<boolean>(false);
    const [isAvatarCheering, setIsAvatarCheering] = useState<boolean>(false);
    const [renderDilemmaCardsInThisPhase, setRenderDilemmaCardsInThisPhase] = useState<boolean>(true);
    const [renderInitialCoins, setRenderInitialCoins] = useState<boolean>(false);
    const [displayModalData, setDisplayModalData] = useState(undefined);
    const [areSolutionCardsDrawnContext, setAreSolutionCardsDrawnContext] = useState<boolean>(false);
    const [isStrengthCardsComponentShown, setIsStrengthCardsComponentShown] = useState<boolean>(false);
    const [isWinnersPodiumShown, setIsWinnersPodiumShown] = useState<boolean>(false);
    const [soundVolume, setSoundVolume] = useState<number>(0.07);
    const [instructionTutorial, setInstructionTutorial] = useState<string>("");
    const [isDilemmaOwnerAnouncementDone, setIsDilemmaOwnerAnouncementDone] = useState<TAnnouncementStates>("");
    const [isCurrentlyPhase16, setIsCurrentlyPhase16] = useState<boolean>(false);
    const [areStrengthsShownFullScreen, setAreStrengthsShownFullScreen] = useState<boolean>(false);
    const [areYouSureBoxRendered, setAreYouSureBoxRendered] = useState<IAlertBoxState>({
        isBoxActive: false, 
        pendingFunctionName: "",
        message: ""
    });
    const [sendMessageToTheUser, setSendMessageToTheUser] = useState<any>({ selectedUserEmail: "", isMessageBoxOpen: false });

    const showStatistics = () => {
        setIsStatisticsShown(true);
    }

    const providerValue = {
        isSurveyShown,
        isStatisticsShown,
        isGameInPostGamePhase, 
        isGoodbyePageShown,
        chatMessages,
        isSpinClicked,
        jokerClicked,
        isUserInPhaseSeven,
        didUserRejoinTheGame,
        isWheelSpinningDone,
        isInitialSolutionDecksModalOpen,
        isAvatarCheering,
        renderDilemmaCardsInThisPhase,
        renderInitialCoins, 
        displayModalData, 
        areSolutionCardsDrawnContext, 
        isStrengthCardsComponentShown, 
        isWinnersPodiumShown, 
        soundVolume,
        areYouSureBoxRendered, 
        instructionTutorial, 
        isDilemmaOwnerAnouncementDone, 
        isCurrentlyPhase16, 
        areStrengthsShownFullScreen, 
        sendMessageToTheUser,
        showStatistics,
        setChatMessages,
        setIsSpinClicked,
        setJokerClicked,
        setIsUserInPhaseSeven,
        setDidUserRejoinTheGame,
        setIsWheelSpinningDone,
        setIsInitialSolutionDecksModalOpen,
        setIsAvatarCheering,
        setRenderDilemmaCardsInThisPhase,
        setRenderInitialCoins,
        setDisplayModalData,
        setisGameInPostGamePhase,
        setIsGoodbyePageShown,
        setAreSolutionCardsDrawnContext,
        setIsStrengthCardsComponentShown,
        setIsWinnersPodiumShown,
        setSoundVolume,
        setAreYouSureBoxRendered,
        setInstructionTutorial,
        setIsDilemmaOwnerAnouncementDone,
        setIsCurrentlyPhase16,
        setAreStrengthsShownFullScreen,
        setIsSurveyShown,
        setSendMessageToTheUser
    };

    return (
        <BoardContext.Provider value={providerValue}>
            {props.children}
        </BoardContext.Provider>
    );
};

export { BoardContext, BoardContextProvider };